import React from 'react';
import { Play, Pause, Volume2, Mic } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '../../ui/button';
import type { AppConfig } from '@/lib/types';

interface WaveformControlsProps {
  config: AppConfig;
  isPlaying: boolean;
  currentTime: number;
  duration: number;
  musicContainerRef: React.RefObject<HTMLDivElement | null>;
  voContainerRef: React.RefObject<HTMLDivElement | null>;
  onTogglePlay: () => void;
  onSeek: (time: number) => void;
  onUpdateConfig: (config: AppConfig) => void;
}

export function WaveformControls({
  config,
  isPlaying,
  currentTime,
  duration,
  musicContainerRef,
  voContainerRef,
  onTogglePlay,
  onSeek,
  onUpdateConfig,
}: WaveformControlsProps) {

  const formatTime = (time: number) => {
    const mins = Math.floor(time / 60);
    const secs = Math.floor(time % 60);
    const ms = Math.floor((time % 1) * 10);
    return `${mins}:${secs.toString().padStart(2, '0')}.${ms}`;
  };

  const handleVolumeChange = (track: 'music' | 'voiceover', value: number) => {
    onUpdateConfig({
      ...config,
      audio: {
        ...config.audio,
        [track === 'music' ? 'musicVolume' : 'voiceoverVolume']: value
      }
    });
  };

  return (
    <div className="flex flex-col gap-5 bg-background/80 p-6 rounded-3xl border border-white/5 backdrop-blur-2xl">
      {/* Playback & Volume Mixed Row */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-8">
        <div className="flex items-center gap-5">
          <Button
            size="icon"
            className="size-14 rounded-2xl bg-primary shadow-2xl shadow-primary/40 hover:scale-105 active:scale-95 transition-all"
            onClick={onTogglePlay}
          >
            {isPlaying ? <Pause className="size-7 fill-current" /> : <Play className="size-7 fill-current ml-1" />}
          </Button>
          <div className="flex flex-col gap-1">
            <div className="flex items-baseline gap-2">
              <span className="text-3xl font-mono font-black text-white tracking-tighter">
                {formatTime(currentTime)}
              </span>
              <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest opacity-30">
                / {formatTime(duration)}
              </span>
            </div>
          </div>
        </div>

        <div className="flex-1 max-w-md flex flex-col gap-3 bg-white/3 p-4 rounded-2xl border border-white/5">
          {/* Voiceover Volume */}
          <div className="flex items-center gap-4">
            <Mic className="size-3.5 text-amber-500/80" />
            <input
              type="range"
              min="0"
              max="1"
              step="0.01"
              className="flex-1 studio-slider accent-amber-500"
              value={config.audio.voiceoverVolume}
              onChange={(e) => handleVolumeChange('voiceover', parseFloat(e.target.value))}
            />
            <span className="text-[10px] font-mono w-10 text-right font-bold text-amber-500/80">{Math.round(config.audio.voiceoverVolume * 100)}%</span>
          </div>

          {/* Music Volume */}
          <div className="flex items-center gap-4">
            <Volume2 className="size-3.5 text-blue-500/80" />
            <input
              type="range"
              min="0"
              max="1"
              step="0.01"
              className="flex-1 studio-slider accent-blue-500"
              value={config.audio.musicVolume}
              onChange={(e) => handleVolumeChange('music', parseFloat(e.target.value))}
            />
            <span className="text-[10px] font-mono w-10 text-right font-bold text-blue-500/80">{Math.round(config.audio.musicVolume * 100)}%</span>
          </div>
        </div>
      </div>

      {/* Waveforms */}
      <div className="flex flex-col gap-1.5 relative">
        {/* Playhead line */}
        <div
          className="absolute top-0 bottom-0 w-px bg-primary z-30 pointer-events-none"
          style={{ left: `${(currentTime / Math.max(duration, 0.1)) * 100}%` }}
        >
          <div className="absolute -top-1 -left-1.5 size-3 rounded-full bg-primary shadow-xl shadow-primary/50" />
        </div>

        {/* Existing Markers visualization */}
        {config.segments.map((s) => (
          <div
            key={s.id}
            className="absolute top-0 bottom-0 w-[2px] bg-white/20 z-20 pointer-events-none"
            style={{ left: `${(s.endTime / Math.max(duration, 0.1)) * 100}%` }}
          >
            <div className="absolute -bottom-1 -left-1 size-2 rounded-full bg-white/20" />
          </div>
        ))}

        {/* Voiceover Waveform */}
        <div className="relative group">
          <div className="absolute -left-6 top-1/2 -translate-y-1/2 -rotate-90 text-[7px] font-black uppercase tracking-[0.3em] text-amber-500/30 group-hover:text-amber-500/60 transition-colors">VOICE</div>
          <div 
            ref={voContainerRef} 
            className="h-20 bg-linear-to-b from-amber-500/1 to-amber-500/3 rounded-2xl border border-amber-500/10 overflow-hidden cursor-crosshair active:cursor-grabbing transition-colors hover:border-amber-500/20"
            onClick={(e) => {
              const rect = e.currentTarget.getBoundingClientRect();
              const x = e.clientX - rect.left;
              onSeek((x / rect.width) * duration);
            }}
          />
        </div>

        {/* Music Waveform */}
        <div className="relative group">
          <div className="absolute -left-6 top-1/2 -translate-y-1/2 -rotate-90 text-[7px] font-black uppercase tracking-[0.3em] text-blue-500/30 group-hover:text-blue-500/60 transition-colors">MUSIC</div>
          <div 
            ref={musicContainerRef} 
            className="h-20 bg-linear-to-b from-blue-500/1 to-blue-500/3 rounded-2xl border border-blue-500/10 overflow-hidden cursor-crosshair active:cursor-grabbing transition-colors hover:border-blue-500/20"
            onClick={(e) => {
              const rect = e.currentTarget.getBoundingClientRect();
              const x = e.clientX - rect.left;
              onSeek((x / rect.width) * duration);
            }}
          />
        </div>
      </div>
    </div>
  );
}
